/* eslint-disable @next/next/no-html-link-for-pages */
import { CarrierCarouselGroups } from "../pages"
import { QUOTES_URL, classNames } from "../utilities"
import { BottomGraphic, FadeIn, FaqSection, Footer, GradientBg } from "./"
import FeaturesThreeCol from "./FeaturesThreeCol"
import { PartnerHeader } from "./PartnerHeader"
import { ProductCarousel } from "./ProductCarousel"
import TestimonialCarousel from "./TestimonialCarousel"
import { ArrowRightIcon } from "@heroicons/react/outline"
import {
  CheckCircleIcon as CheckCircleIconSolid,
  CreditCardIcon,
  CursorClickIcon,
  PhoneIcon,
  ShieldCheckIcon,
} from "@heroicons/react/solid"
import { NextSeo } from "next-seo"
import Image from "next/image"
import { useEffect, useState } from "react"
import { Carousel } from "react-responsive-carousel"
import "react-responsive-carousel/lib/styles/carousel.min.css"

interface PartnerPage {
  partner: string
  partnerLogoUrl: string
  cobrandedImageUrl?: string
  partnerLogoClassName?: string
  percentage?: number
  license?: string
  companyType?: string
  businessClassificationId?: string
}

export function PartnerPageFreelancer({
  partner,
  partnerLogoUrl,
  cobrandedImageUrl,
  partnerLogoClassName = "max-h-8 ml-2",
  percentage = 25,
  license,
  businessClassificationId,
}: PartnerPage) {
  const heroFeatures = [
    `Savings of up to ${percentage}% on freelancer insurance coverage`,
    "Free quotes from 30+ insurers instantly",
  ]
  const subFeatures = [
    {
      name: "Instant Quotes",
      description: "Get quotes in seconds",
      href: "#",
      icon: CursorClickIcon,
    },
    {
      name: "Top-tier pricing",
      description: `Compare coverages from the nation's leading insurers`,
      href: "#",
      icon: CreditCardIcon,
    },
    {
      name: "Complete Coverage",
      description: "Fulfill your needs and requirements",
      href: "#",
      icon: ShieldCheckIcon,
    },
    {
      name: "Expert Service",
      description: "24/7 support from our team of experts",
      href: "#",
      icon: PhoneIcon,
    },
  ]
  const testimonials = [
    {
      companyLogo:
        "https://uploads-ssl.webflow.com/6245b1913c8b592dd2e107ec/625423b9a25b9541879b8e0f_option%204.1.svg",
      review:
        "The Coverdash team was extremely knowledgeable and honest when helping us assess what coverage we actually needed, and ultimately saved us 30% compared to other quotes which were unnecessarily broad. We'd highly recommend them to any other companies.",
      name: "Henry Magun",
      title: "CEO of Hansa",
    },
    {
      companyLogo:
        "https://res.cloudinary.com/dcslbsrur/image/upload/v1692033059/images/mqkqulodjydcaonl70vq.jpg",
      review:
        "We needed general liability and E&O policies after signing our first large vendor contract. That very same day I was able to get fully covered with COI in hand from Coverdash. They are truly exceptional at what they do.",
      name: "Meyer Tawil",
      title: "CEO of Pivot Accessories",
    },
    {
      companyLogo:
        "https://res.cloudinary.com/dcslbsrur/image/upload/v1692050119/images/dxtbwfdlc7ogy6oambob.png",
      review:
        "Acquiring insurance through Coverdash was as easy as it could possibly be. I would not have put it off if I knew it would be that easy!!",
      name: "Joe Grodko",
      title: "CEO of Oval Ventures",
    },
    {
      companyLogo:
        "https://res.cloudinary.com/dcslbsrur/image/upload/v1692033227/images/aioepfm9anuwltvexs7u.png",
      review:
        "I had to secure a general liability policy before signing a new lease for our operations. Coverdash was able to provide multiple quote options and get coverage in place the next day. Recommended.",
      name: "Ike Tobias",
      title: "Founder at Martinique",
    },
    {
      companyLogo:
        "https://nubrakes.com/_next/static/media/nubrakes-logo.c29e0ed5.svg",
      review:
        "I considered Coverdash and 3 other brokers; Coverdash was the only broker who had quotes back to me within 24 hours AND saved us ~50% compared to our premium from the prior year. However, cost savings wasn't my primary driver.... I wanted a broker who offered an all-in-one online portal to manage policies, COI's, claims, etc. My 'legacy broker' dropped the ball too many times due to a lack of organization and poor communication. Coverdash solves all of these problems! As someone who has had 3 brokers in the last 6 years.. and considered many others, Coverdash is undoubtedly in a league of their own.",
      name: "Walker Drewett",
      title: "CEO of NuBrakes",
    },
  ]

  useEffect(() => {
    localStorage.setItem("referrer", partner.toLowerCase().replace(/ /g, "-"))
    if (license) {
      localStorage.setItem("licenseId", license)
    }
  }, [])
  const quoteUrl = license
    ? `${QUOTES_URL}?license=${license}&prefilled_companyType=freelancer${
        businessClassificationId
          ? `&prefilled_businessClassificationId=${businessClassificationId}`
          : ""
      }`
    : "/quote"

  const [canonicalUrl, setCanonicalUrl] = useState<string>(
    `https://www.coverdash.com/partners/${partner
      .toLowerCase()
      .replace(/ /g, "-")}`
  )
  useEffect(() => {
    setCanonicalUrl(window.location.href)
  }, [])

  const FALLBACK_COBRANDED_IMAGE_URL = `https://coverdash.s3.amazonaws.com/images/cobrand-logos-${partner
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(".", "-")}.jpg`
  return (
    <>
      <NextSeo
        title={`${partner} × Coverdash`}
        description={`${partner} × Coverdash`}
        canonical={canonicalUrl}
        openGraph={{
          title: `${partner} × Coverdash`,
          description: `${partner} × Coverdash`,
          images: [
            {
              url: cobrandedImageUrl || FALLBACK_COBRANDED_IMAGE_URL,
              width: 800,
              height: 418,
              alt: `${partner} × Coverdash`,
            },
            {
              url: cobrandedImageUrl || FALLBACK_COBRANDED_IMAGE_URL,
              width: 1200,
              height: 627,
              alt: `${partner} × Coverdash`,
            },
          ],
        }}
      />
      {/* <Head>
        <title>{`${partner} × Coverdash`}</title>

        <meta property="og:title" content={`${partner} × Coverdash`} />
        <meta
          property="og:image"
          content={`https://coverdash.s3.amazonaws.com/images/cobrand-logos-${partner
            .toLowerCase()
            .replace(/ /g, "-")}.jpg`}
        />
      </Head> */}
      <GradientBg />
      <PartnerHeader
        partnerLogoUrl={partnerLogoUrl}
        partnerLogoClassName={partnerLogoClassName}
        quoteUrl={quoteUrl}
      />
      {/* <Hero /> */}
      {/* Hidden IFrame to set partnerLicenseName in localStorage */}
      <iframe
        src={`${QUOTES_URL}?license=${license}&reset=true`}
        // src={`http://local.coverdash.com:3001?license=${license}&reset=true`}
        className="h-0 w-0"
      ></iframe>
      <div className="relative isolate overflow-hidden bg-white">
        <svg
          className="absolute inset-0 -z-10 h-full w-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="0787a7c5-978c-4f66-83c7-11c213f99cb7"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)"
          />
        </svg>
        <div className="mx-auto max-w-7xl px-6 pt-10 pb-14 sm:pb-32 grid lg:flex lg:py-40 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8">
            {/* <img
              className="h-11"
              src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
              alt="Your Company"
            /> */}
            {/* <div className="mt-24 sm:mt-32 lg:mt-16">
            <a href="#" className="inline-flex space-x-6">
              <span className="rounded-full bg-indigo-600/10 px-3 py-1 text-sm font-semibold leading-6 ring-1 ring-inset ring-indigo-600/10">
                What's new
              </span>
              <span className="inline-flex items-center space-x-2 text-sm font-medium leading-6 text-gray-600">
                <span>Just shipped v1.0</span>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            </a>
          </div> */}
            <FadeIn delay={0.3}>
              <div
                className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
                aria-hidden="true"
              >
                <div
                  className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#f8fafc] to-primary opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                  style={{
                    clipPath:
                      "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
                  }}
                />
              </div>
              <h1 className="mt-10 text-2xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">
                Exclusive insurance savings for {partner} freelancers
              </h1>
            </FadeIn>
            <FadeIn delay={0.5}>
              <div className="mt-6 text-lg leading-8 text-gray-600">
                {partner} freelancers are now eligible for:
                <br />
                <ul
                  role="list"
                  className={classNames("my-2 text-base tracking-tight")}
                >
                  {heroFeatures.map((feature) => (
                    <li key={feature} className="flex py-2">
                      <CheckCircleIconSolid
                        className={classNames("h-5 w-5 flex-none text-primary")}
                      />
                      <span className="ml-2">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
            <div className="mt-10 items-center gap-x-6">
              <FadeIn delay={0.55}>
                <div className="rounded-md mt-4 sm:mt-0">
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={quoteUrl}
                    className="w-full md:w-auto btn-primary"
                  >
                    <span>Get a quote</span>
                    <ArrowRightIcon className="h-4 w-4 inline" />
                  </a>
                </div>
              </FadeIn>
            </div>
          </div>
          <div className="order-first sm:order-none z-50 mx-auto mt-16 flex max-w-2xl sm:mt-24 lg:ml-10 lg:mt-0 lg:mr-0 lg:max-w-none lg:flex-none xl:ml-32">
            {/* <div className="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none"> */}
            {/* <div className="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4"> */}
            <FadeIn delay={0.55}>
              <img
                src={cobrandedImageUrl || FALLBACK_COBRANDED_IMAGE_URL}
                alt={`${partner} × Coverdash`}
                width={2432}
                height={1442}
                className="w-full sm:w-auto max-w-2xl rounded-md shadow-lg ring-1 ring-gray-900/10"
              />
            </FadeIn>
            {/* </div> */}
            {/* </div> */}
          </div>
        </div>
        <div
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#f8fafc] to-primary opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
      </div>
      {/* Coverdash top feature */}
      <div className="bg-white border-y border-slate-200 pt-6 sm:pt-16">
        <div className="mx-auto px-8 md:px-0">
          <FadeIn>
            <h2 className="tracking-tight text-2xl sm:text-4xl mx-auto max-w-5xl text-alt font-extrabold my-7 text-center">
              Need freelancer insurance?
            </h2>
            <h3 className="tracking-tight -mt-4 text-3xl sm:text-5xl mx-auto max-w-5xl text-alt font-extrabold my-7 text-center">
              Meet <span className="text-primary">Coverdash.</span>
            </h3>
          </FadeIn>
          <FeaturesThreeCol features={subFeatures} />

          {/* <div className="mt-10 bg-white pb-6 sm:pb-8">
            <div className="relative">
              <div className="absolute inset-0 h-1/2 bg-[#f1fff9]" />
              <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
                <div className="mx-auto max-w-7xl">
                  <dl className="rounded-lg bg-white shadow-lg sm:grid sm:grid-cols-2 md:grid-cols-4">
                    <div className="flex flex-col border-b border-gray-100 p-6 text-center sm:border-0 sm:border-r">
                      <dt className="order-2 mt-2 text-base font-medium leading-6 text-gray-500">
                        Get quotes in seconds
                      </dt>
                      <dd className="order-1 text-xl font-bold tracking-tight">
                        Instant Quotes
                      </dd>
                    </div>
                    <div className="flex flex-col border-t border-b border-gray-100 p-6 text-center sm:border-0 sm:border-l sm:border-r">
                      <dt className="order-2 mt-2 text-base font-medium leading-6 text-gray-500">
                        Compare coverages from the nation's leading insurers
                      </dt>
                      <dd className="order-1 text-xl font-bold tracking-tight">
                        Top-tier Pricing
                      </dd>
                    </div>
                    <div className="flex flex-col border-t border-gray-100 p-6 text-center sm:border-0 sm:border-l">
                      <dt className="order-2 mt-2 text-base font-medium leading-6 text-gray-500">
                        Fulfill your needs and requirements
                      </dt>
                      <dd className="order-1 text-xl font-bold tracking-tight">
                        Complete Coverage
                      </dd>
                    </div>
                    <div className="flex flex-col border-t border-gray-100 p-6 text-center sm:border-0 sm:border-l">
                      <dt className="order-2 mt-2 text-base font-medium leading-6 text-gray-500">
                        24/7 support from our team of experts
                      </dt>
                      <dd className="order-1 text-xl font-bold tracking-tight">
                        Expert Service
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
            <h3 className="mt-16 text-2xl sm:text-4xl mx-auto max-w-5xl text-alt font-extrabold my-7 text-center">
              Business insurance in a{" "}
              <span className="text-primary">dash.</span>
            </h3>
            F
          </div> */}
        </div>
      </div>
      {/* 3 simple steps to instant coverage */}
      <div className="bg-[#fbfbfb] border-y border-slate-200 py-16">
        <div className="container mx-auto px-8 xl:px-32">
          <FadeIn>
            <h2 className="text-2xl sm:text-5xl text-alt font-extrabold my-7 text-center">
              3 Simple steps to <span className="text-primary">instant</span>{" "}
              coverage
            </h2>

            <div className="text-center text-base sm:text-xl text-gray-500 mt-4">
              Fast, easy, and reliable coverage that reaches freelancers where
              they are, when they need it
            </div>
          </FadeIn>

          <div className="md:grid grid-cols-3 gap-10 mt-16">
            <FadeIn>
              <div className="grid grid-cols-4 md:grid-cols-1 gap-3 md:gap-0 md:justify-items-center md:text-center mb-12">
                <Image
                  src="/img/step1.svg"
                  alt="Illustration of lady with tablet"
                  width={325}
                  height={325}
                  aria-hidden="true"
                />
                <div
                  className="lg:mt-5 grid col-span-3 items-center text-2xl font-semibold text-gray-600"
                  style={{ gridTemplateColumns: "max-content 1fr" }}
                >
                  <div className="text-6xl font-bold text-gray-300 mr-4 sm:mr-2">
                    1
                  </div>
                  Tell us what you do
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2}>
              <div className="grid grid-cols-4 md:grid-cols-1 gap-3 md:gap-0 md:justify-items-center md:text-center mb-12">
                <Image
                  src="/img/step2.svg"
                  alt="Illustration of man pointing at tablet"
                  width={325}
                  height={325}
                  aria-hidden="true"
                />
                <div
                  className="lg:mt-5 grid col-span-3 items-center text-2xl font-semibold text-gray-600"
                  style={{ gridTemplateColumns: "max-content 1fr" }}
                >
                  <div className="text-6xl font-bold text-gray-300 mr-4 sm:mr-2">
                    2
                  </div>
                  Select your policy
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.4}>
              <div className="grid grid-cols-4 md:grid-cols-1 gap-3 md:gap-0 md:justify-items-center md:text-center mb-12">
                <Image
                  src="/img/step3.svg"
                  alt="Illustration of many holding phone"
                  width={325}
                  height={325}
                  aria-hidden="true"
                />
                <div
                  className="lg:mt-5 grid col-span-3 items-center text-2xl font-semibold text-gray-600"
                  style={{ gridTemplateColumns: "max-content 1fr" }}
                >
                  <div className="text-6xl font-bold text-gray-300 mr-4 sm:mr-2">
                    3
                  </div>
                  Get instant coverage
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
      {/* Explore products */}
      <div className="bg-white py-16 px-12 max-w-7xl mx-auto">
        <div className="mx-auto container">
          <FadeIn>
            <h2 className="text-2xl sm:text-5xl text-alt font-extrabold my-7 text-center ">
              Explore our freelancer insurance{" "}
              <span className="text-primary">products</span>
            </h2>
            {/* <div className="text-center sm:w-1/2 mx-auto mt-4 text-base sm:text-lg text-gray-500">
              At Coverdash, we understand how to protect your business and can
              provide – tailored, low-cost and hassle-free insurance coverage
            </div> */}
          </FadeIn>
        </div>
        <FadeIn>
          <ProductCarousel
            products={[
              "General Liability",
              "Business Owner's Policy",
              "Professional Liability (E&O)",
              "Cyber",
              "Workers' Compensation",
            ]}
            quoteUrl={quoteUrl}
          />
        </FadeIn>
      </div>
      <div className="bg-slate-900 pt-16 pb-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl font-bold text-slate-100">
            Our carrier partners
          </h2>
          <Carousel
            className="mt-12"
            showStatus={false}
            showArrows={false}
            showIndicators={false}
            stopOnHover={false}
            interval={5000}
            transitionTime={1500}
            infiniteLoop
            autoPlay
          >
            {CarrierCarouselGroups.map((group, index) => (
              <div
                key={index}
                className="grid grid-cols-2 md:grid-cols-7 gap-8 px-4"
              >
                {group.map((item, idx) => (
                  <div
                    key={idx}
                    className="col-span-1 flex justify-center md:col-span-2 lg:col-span-1"
                  >
                    {item === "atbay.png" ? (
                      <img
                        style={{
                          width: "100px",
                          marginLeft: "-3rem",
                        }}
                        className={classNames("h-12")}
                        src={`/img/carriers/${item}`}
                        alt={`${item
                          .replace(".png", "")
                          .replace(".svg", "")} logo`}
                      />
                    ) : (
                      <img
                        className={classNames("h-12")}
                        src={`/img/carriers/${item}`}
                        alt={`${item
                          .replace(".png", "")
                          .replace(".svg", "")} logo`}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </Carousel>
        </div>
      </div>
      <div className="mt-20 mx-auto max-w-xl text-center">
        <FadeIn>
          <h2 className="text-2xl sm:text-5xl text-alt font-extrabold text-center ">
            Freelancer <span className="text-primary">Testimonials</span>
          </h2>
          {/* <div className="text-center sm:w-1/2 mx-auto mt-4 text-base sm:text-lg text-gray-500">
              At Coverdash, we understand how to protect your business and can
              provide – tailored, low-cost and hassle-free insurance coverage
            </div> */}
        </FadeIn>
      </div>
      {/* <Testimonials /> */}
      <TestimonialCarousel testimonials={testimonials} />
      <FaqSection title="Freelancer insurance" faqs={faqs} />
      <BottomGraphic />
      <Footer />
    </>
  )
}

const faqs = [
  {
    question: "Why do I need freelancer insurance?",
    answer: `As a freelancer, you don't have the benefit of working under the umbrella of an employer's insurance coverage. No matter what type of freelance work you're doing, every profession comes with a particular set of common risks. Obtaining the right insurance is crucial to protect your business.`,
  },
  {
    question: "What kind of freelancers does Coverdash provide coverage for?",
    answer: `Coverdash specializes in providing coverage for all types of businesses, regardless of your industry or size.`,
  },
  {
    question: "How long does it take to get covered?",
    answer: `You can obtain quotes and purchase your policy within minutes. Timing of coverage varies based on the complexity of your specific business and the coverage you're requesting, but typically coverage is put into effect instantly.`,
  },
  {
    question: "Is freelancer insurance tax deductible?",
    answer: `Yes, freelancer insurance is tax deductible given that is a cost of doing business.`,
  },
  {
    question: "How can I get access to certificates of insurance?",
    answer: `You can instantly access your existing certificates or generate new ones at any time when you log into your personal dashboard. Best part is that it is totally free!`,
  },
]
